<template lang="pug">
.abonement-list.pagination
    paginated-items(
        scope="base"
        type="cms"
        :loading="loading"
        :items="cmssPaginated.items"
        :show-filters="showFilters"
        :filters="cmssPaginated.filters"
        :variables="cmssPaginated.variables"
        :all-columns="cmssPaginated.allColumns"
        :active-columns="cmssPaginated.activeColumns"
        :page-info="cmssPaginated.pageInfo"
        :sort="sort"
        @pagination-size-change="handleSizeChange"
        @pagination-current-change="handleCurrentChange"
        @show-table-columns="handleColsShow"
        @change-filter="handleFilterInputChange"
        @click="handleClick"
        @change-sort="handleSortInputChange"
    )
        div(slot="top")
            el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                .row
                    .left
                        el-button(
                            size="mini"
                            type="primary"
                            icon="el-icon-plus"
                            @click="goToNewCms"
                        ) {{ $t('New CMS') }}
                    .middle.hide-on-empty
                        template(v-if="isSuperuser && (bulk.action || cmssPaginated.pageInfo.selection.total > 0)")
                            span.info {{ $t('{total} selected', { total: cmssPaginated.pageInfo.selection.total }) }}
                    .right.hide-on-empty
                        .bulk-actions(v-if="isSuperuser")
                            el-select(
                                v-model="bulk.action"
                                size="mini"
                                :placeholder="$t('Select bulk action...')"
                                clearable
                            )
                                el-option(
                                    v-for="item in bulk.options"
                                    :key="item.value"
                                    :label="item.label"
                                    :value="item.value"
                                )
                            transition(name="slide-decent-left")
                                el-button(
                                    v-if="cmssPaginated.pageInfo.selection.total > 0 || bulk.action"
                                    type="primary"
                                    size="mini"
                                    :disabled="!bulk.action || cmssPaginated.pageInfo.selection.total <= 0"
                                    icon="el-icon-fa-check fas"
                                    @click.stop.prevent="handleBulkAction()"
                                )
                                    | {{ $t('Go') }}
                        hr.vertical
                        el-button(
                            plain
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-filter fas"
                            @click="toggleFilterVisiblity"
                            :disabled="disable.showFilters || hasFilters"
                        )
                            span(v-if="showFilters") {{ $t('Hide filters') }}
                            span(v-else) {{ $t('Show filters') }}
        template(v-slot:actions:th)
            .col-left(v-if="isSuperuser && !showFilters")
                check-all(
                    :total="cmssPaginated.pageInfo.total"
                    :show-dropdown="cmssPaginated.pageInfo.lastPage > 1"
                    :checked="bulkSelection"
                    :is-indeterminate="selectionIndeterminate"
                    @check-all="checkAll"
                    @check-visible="checkVisible"
                )
        template(v-slot:actions:filter)
            .col-left(v-if="isSuperuser && showFilters" style="padding-left: 10px")
                check-all(
                    :total="cmssPaginated.pageInfo.total"
                    :show-dropdown="cmssPaginated.pageInfo.lastPage > 1"
                    :checked="bulkSelection"
                    :default-event="checkAllEvent"
                    :is-indeterminate="selectionIndeterminate"
                    @check-all="checkAll"
                    @check-visible="checkVisible"
                )
        template(v-slot:actions="{ item }")
            .actions-row
                .col-left(v-if="isSuperuser")
                    el-checkbox(
                        :key="item.id"
                        :value="isChecked(item.id)"
                        @change="checkItem(item)"
                    )
                el-button.copy-button(
                    v-if="!copiedSettings"
                    circle
                    size="mini"
                    type="info"
                    icon="el-icon-fa-copy far"
                    @click.stop.prevent="toggleCopySettings(item.id)"
                )
                el-button.copy-button(
                    v-else-if="parseInt(item.id) === parseInt(copiedSettings)"
                    circle
                    size="mini"
                    type="default"
                    icon="el-icon-fa-clipboard fas"
                    @click.stop.prevent="toggleCopySettings(item.id)"
                )
                el-button.copy-button(
                    v-else
                    circle
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-paste fas"
                    @click.stop.prevent="showSettingsClipboardModal(item)"
                )
                el-tooltip(
                    v-if="isSuperuser && item && item.owner && item.owner.user && user.id !== item.owner.user.id"
                    effect="dark"
                    :content="$t('Switch to this CMS owner')"
                    placement="top"
                )
                    el-button.mimic-button(
                        v-if="canMimic(item)"
                        circle
                        plain
                        size="mini"
                        type="info"
                        icon="el-icon-fa-right-to-bracket fas"
                        @click.stop.prevent="mimicOwner(item)"
                    )
        template(v-slot:indicators:th)
            .col-center
                .condensed-indicators
                    span.indicator(
                        v-for="indicator in options.indicator.items"
                        :class="indicator.type"
                    )

        template(v-slot:indicators:filter:option-template="{ item }")
            .flex.space-between
                span {{ item.label }}
                span.indicator(:class="item.key")

        template(v-slot:indicators="{ value }")
            nobr.col-center(v-if="value && value.length")
                .indicators
                    span.indicator(v-for="indicator in value.slice().sort(function(a, b) { return a.id - b.id })" :class="indicator.type")

        template(v-slot:status="{ value }")
            span(v-if="value") {{ $t(value) }}
        template(v-slot:fee="{ value }")
            span(v-if="value") {{ value | moneyFormat('EUR') }}
        template(v-slot:domain="{ value }")
            span(v-if="value") {{ getBaseUrl(value) }}
    el-dialog.overwrite-cms-settings-dialog(
        :title="$t('Copy CMS settings')"
        :visible.sync="settingsClipboardModal.visibility"
        :close-on-click-modal="false"
        top="10vh"
        width="520px"
        @closed="resetSettingsClipboardModal"
    )
        .body(v-loading="settingsClipboardModal.loading")
            p {{ $t('Are you sure you want to overwrite this CMS settings?') }}
        template(slot="footer")
            .modal-footer
                .left
                    el-checkbox(v-model="settingsClipboardModal.includeImages") {{ $t('Include images') }}
                .right
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="settingsClipboardModal.visibility = false"
                    ) {{ $t('Dismiss') }}
                    el-button(
                        :loading="settingsClipboardModal.pasting"
                        size="mini"
                        type="danger"
                        icon="el-icon-fa-check fas"
                        @click="pasteSettings"
                        :disabled="settingsClipboardModal.pasting"
                    ) {{ $t('Overwrite irreversible') }}
    el-dialog.bulk-action-dialog(
        :title="bulk.modal.title"
        :visible.sync="bulk.modal.visibility"
        :close-on-click-modal="false"
        :width="bulk.modal.width"
        top="10vh"
        @closed="resetBulkModal"
    )
        .bulk-modal-body.indicators(v-if="bulk.action === 'change-indicators' && bulk.modal.data && bulk.modal.data.indicators")
            a.indicator.is-outlined(
                v-for="indicator in options.indicator.items"
                :key="indicator.id"
                :class="[ indicator.type, { active: bulk.modal.data.indicators.find(function(element) { return element.id === indicator.id }) } ]"
                href="#"
                @click.prevent.stop="toggleBulkModalIndicator(indicator)"
            )
                i.el-icon-fa-check.fas(v-if="bulk.modal.data.indicators.find(function(element) { return element.id === indicator.id })")

        template(slot="footer")
            .modal-footer
                .left
                    el-button(
                        v-for="button in bulk.modal.buttons.left"
                        :key="button.label"
                        v-if="button.action"
                        :size="button.size || 'mini'"
                        :type="button.type"
                        :icon="button.icon"
                        @click="button.action"
                    ) {{ button.label }}
                .right
                    el-button(
                        v-for="button in bulk.modal.buttons.right"
                        :key="button.label"
                        v-if="button.action"
                        :size="button.size || 'mini'"
                        :type="button.type"
                        :icon="button.icon"
                        @click="button.action"
                    ) {{ button.label }}
</template>

<i18n>
{
    "en": {
        "Change color indicators...": "Change color indicators...",
        "Select desired color indicators": "Select desired color indicators",
        "{total} selected": "{total} selected",
        "trial": "Trial",
        "acquisition": "Acquisition",
        "draft": "Draft",
        "pro bono": "Pro Bono",
        "billing": "Billing",
        "terminated": "Terminated",

        "monthly": "Monthly",
        "quarterly": "Quarterly",
        "annually": "Annually",

        "annually-jan": "Annually January",
        "annually-feb": "Annually February",
        "annually-mar": "Annually March",
        "annually-apr": "Annually April",
        "annually-may": "Annually May",
        "annually-jun": "Annually June",
        "annually-jul": "Annually July",
        "annually-aug": "Annually August",
        "annually-sep": "Annually September",
        "annually-oct": "Annually October",
        "annually-nov": "Annually November",
        "annually-dec": "Annually December",
        "Switch to this CMS owner": "Switch to this CMS owner"
    },
    "de": {
        "Change color indicators...": "Farbmarker ändern...",
        "Select desired color indicators": "Gewünschte Farbmarker auswählen",
        "Select bulk action...": "Massenaktion auswählen...",
        "{total} selected": "{total} ausgewählt",
        "Go": "Los",
        "Bulk action processed successfully!": "Massenaktion erfolgreich verarbeitet!",
        "Error processing bulk action": "Fehler bei der Verarbeitung der Massenaktion",
        "Dismiss": "Abbrechen",
        "Remove": "Entfernen",
        "Add": "Hinzufügen",
        "New CMS": "Neues CMS",
        "New Account": "Neuer Account",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Switch to this CMS owner": "Zum Inhaber dieses CMS wechseln",
        "Account list could not be fetched.": "Benutzerliste konnte nicht abgerufen werden.",
        "Copy CMS settings": "CMS Einstellungen kopieren",
        "Are you sure you want to overwrite this CMS settings?": "Sollen diese CMS Einstellungen wirklich überschrieben werden?",
        "CMS settings copied successfully!": "CMS Einstellungen wurden erfolgreich kopiert",
        "CMS settings pasted successfully!": "CMS Einstellungen wurden erfolgreich eingefügt",
        "CMS settings removed from clipboard!": "CMS Einstellungen aus dem Zwischenspeicher entfernt",
        "Overwrite irreversible": "Unwiderruflich überschreiben",
        "CMS could not be saved.": "CMS konnte nicht gespeichert werden.",
        "Include images": "Bilder einbeziehen",
        "Hosting": "Hosting",
        "Service": "Service",
        "Total": "Gesamt",
        "trial": "Testphase",
        "acquisition": "Akquise",
        "draft": "Entwurf",
        "pro bono": "Pro Bono",
        "billing": "Abrechnung",
        "terminated": "Gekündigt",
        "monthly": "Monatlich",
        "quarterly": "Quartalsweise",
        "annually": "Jährlich",
        "annually-jan": "Jährlich Januar",
        "annually-feb": "Jährlich Februar",
        "annually-mar": "Jährlich März",
        "annually-apr": "Jährlich April",
        "annually-may": "Jährlich Mai",
        "annually-jun": "Jährlich Juni",
        "annually-jul": "Jährlich Juli",
        "annually-aug": "Jährlich August",
        "annually-sep": "Jährlich September",
        "annually-oct": "Jährlich Oktober",
        "annually-nov": "Jährlich November",
        "annually-dec": "Jährlich Dezember",
        "January": "Januar",
        "February": "Februar",
        "March": "März",
        "April": "April",
        "May": "Mai",
        "June": "Juni",
        "July": "Juli",
        "August": "August",
        "September": "September",
        "October": "Oktober",
        "November": "November",
        "December": "Dezember"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { canMimicWebsite } from '@/helpers/mimic';
import { adminClient as apolloClient } from '@/store/apollo';

import router from '@/router';
import pinia from '@/pinia';
import SetupApi from '@/store/api/system/setup';
import { useAuthStore, useSettingsStore, useSetupStore, useTicketStore } from '@/stores';

import CheckAll from '@/components/CheckAll';
import PaginatedItems from '@/components/PaginatedItems';

export default {
    name: 'Abonements',
    data() {
        return {
            disable: {
                showFilters: false,
            },
            settingsClipboardModal: {
                pasting: false,
                visibility: false,
                item: undefined,
                includeImages: false,
            },
            bulk: {
                action: undefined,
                options: [{ value: 'change-indicators', label: this.$t('Change color indicators...') }],
                modal: {
                    visibility: false,
                    width: '400px',
                    title: undefined,
                    body: undefined,
                    data: undefined,
                    buttons: {
                        left: [
                            {
                                size: 'mini',
                                icon: 'el-icon-fa-times fas',
                                label: this.$t('Dismiss'),
                                action: undefined,
                            },
                        ],
                        right: [],
                    },
                },
            },
        };
    },
    components: {
        CheckAll,
        PaginatedItems,
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.cmssPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.cmssPaginated.filters);
        },
        sort() {
            var variables = this.cmssPaginated.variables;
            var defaultSort = this.cmssPaginated.defaultSort;
            var order = _.get(variables, 'order') || _.get(defaultSort, 'field') || 'id';
            var direction = _.get(variables, 'direction') || _.get(defaultSort, 'direction') || 'asc';
            return { field: order, direction: direction };
        },
        copiedSettings() {
            const context = 'user';
            return _.get(this.clipboard, `${context}.cms-settings`);
        },
        user() {
            return useAuthStore().user;
        },
        isSuperuser() {
            return useAuthStore().isSuperUser;
        },
        loading() {
            return useSettingsStore().loading;
        },
        cmssPaginated() {
            return useSettingsStore().cmssPaginated;
        },
        options() {
            return useSettingsStore().options;
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        clipboard() {
            return this.setupStore.clipboard;
        },
        debounce() {
            return this.setupStore.debounce;
        },
        settingsStore() {
            return useSettingsStore();
        },
        bulkSelection() {
            var checked = 'none';
            var total = _.get(this.cmssPaginated, 'pageInfo.total') || 0;
            var selection = this.cmssPaginated.pageInfo.selection;

            if (selection.type === 'exclusive' && selection.identifiers.length < total) checked = 'all';
            else if (selection.type === 'inclusive' && selection.identifiers.length) {
                checked = 'visible';
            }
            return checked;
        },
        selectionIndeterminate() {
            var indeterminate = false;
            var selection = this.cmssPaginated.pageInfo.selection;

            if (selection.type === 'exclusive' && selection.identifiers.length) indeterminate = true;
            else if (selection.type === 'inclusive' && selection.identifiers.length) {
                var items = _.get(this.cmssPaginated, 'items') || [];
                var currentPageSelection = items.filter(function (element) {
                    return selection.identifiers.includes(parseInt(element.id));
                });
                indeterminate = currentPageSelection.length < items.length;
            }
            return indeterminate;
        },
        checkAllEvent() {
            var event = 'check-all';
            if (this.hasFilters) event = 'check-visible';
            return event;
        },
    },
    methods: {
        canMimic(website) {
            return canMimicWebsite(website);
        },

        goToNewCms() {
            router.push({ name: 'system-cms-wizard' });
        },
        addAccount() {
            const params = { id: 'new' };
            router.push({ name: 'system-account-main', params });
        },
        handleClick(item) {
            const params = { id: item.id };
            router.push({ name: 'settings-form', params });
        },
        handleSizeChange(itemsPerPage) {
            useSettingsStore().updateLoading(true);
            useSettingsStore().defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            useSettingsStore().updateLoading(true);
            useSettingsStore().defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            useSettingsStore().defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            useSettingsStore().defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            useSettingsStore().defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            useSettingsStore().defineFilterVisibility(value);
            this.saveFilterVisibility({ scope: 'base', key: 'cms', value }).finally(
                () => (this.disable.showFilters = false)
            );
        },
        getBaseUrl(value) {
            return new URL(value).host;
        },
        refetch() {
            useSettingsStore()
                .fetchCmssPaginated()
                .then(() => useSettingsStore().updateLoading(false))
                .catch((err) => {
                    console.error(err);
                    useSettingsStore().updateLoading(false);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Account list could not be fetched.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                });
        },
        debouncedRefetch: _.debounce(function () {
            useSettingsStore().updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        toggleCopySettings(id) {
            if (parseInt(id) === parseInt(this.copiedSettings)) {
                this.removeFromClipboard({ context: 'user', key: 'cms-settings' });

                this.$notify.warning({
                    title: this.$t('CMS settings removed from clipboard!'),
                    iconClass: 'far el-icon-fa-clipboard',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
            } else {
                this.setClipboard({ key: 'cms-settings', context: 'user', value: parseInt(id) });

                this.$notify.success({
                    title: this.$t('CMS settings copied successfully!'),
                    iconClass: 'fas el-icon-fa-copy',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
            }
        },
        async pasteSettings() {
            this.settingsClipboardModal.pasting = true;

            const payload = {
                source: this.copiedSettings,
                target: this.settingsClipboardModal.item.id,
                includeImages: this.settingsClipboardModal.includeImages,
            };

            this.copyCmsSettings(payload)
                .then(() => {
                    this.resetSettingsClipboardModal();
                    this.removeFromClipboard({ context: 'user', key: 'cms-settings' });

                    this.$notify.success({
                        title: this.$t('CMS settings pasted successfully!'),
                        iconClass: 'fas el-icon-fa-paste',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 6000,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('CMS could not be saved.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .finally(() => {
                    this.settingsClipboardModal.pasting = false;
                });
        },
        showSettingsClipboardModal(item) {
            this.settingsClipboardModal.visibility = true;
            this.settingsClipboardModal.item = item;
        },
        resetSettingsClipboardModal() {
            this.settingsClipboardModal = {
                pasting: false,
                visibility: false,
                item: undefined,
                includeImages: false,
            };
        },
        mimicOwner(item) {
            const ownerId = _.get(item, 'owner.user.id');
            if (!ownerId) return;

            // Pre-set the CMS ID so it gets selected after mimic
            SetupApi.setActiveCmsId(item.id);

            // Mimic the owner — through the support-ticket dialog
            useTicketStore().requestMimic({
                userId: ownerId,
                cmsOptions: [{ id: item.id, name: item.name }],
            });
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        copyCmsSettings(payload) {
            return this.setupStore.copyCmsSettings(payload);
        },
        setClipboard(payload) {
            this.setupStore.setClipboard(payload);
        },
        removeFromClipboard(payload) {
            this.setupStore.removeFromClipboard(payload);
        },
        fetchIndicatorOptions() {
            return useSettingsStore().fetchIndicatorOptions();
        },
        checkAll(payload) {
            useSettingsStore().paginationBulkSelectionCheckAll(payload);
        },
        checkVisible(payload) {
            useSettingsStore().paginationBulkSelectionCheckVisible(payload);
        },
        checkItem(item) {
            useSettingsStore().paginationBulkSelectionCheckItem(item);
        },
        isChecked(id) {
            var checked = false;
            var selection = this.cmssPaginated.pageInfo.selection;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers
                        .map(function (element) {
                            return parseInt(element);
                        })
                        .indexOf(parseInt(id));
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers
                        .map(function (element) {
                            return parseInt(element);
                        })
                        .indexOf(parseInt(id));
                    break;
            }
            return checked;
        },
        handleBulkAction(action) {
            var self = this;
            if (!action) action = this.bulk.action;

            switch (action) {
                case 'change-indicators':
                    this.bulk.modal.title = this.$t('Select desired color indicators');
                    this.bulk.modal.width = '400px';
                    this.bulk.modal.data = { indicators: [] };

                    this.bulk.modal.buttons.left = [
                        {
                            size: 'mini',
                            icon: 'el-icon-fa-times fas',
                            label: this.$t('Dismiss'),
                            action: function () {
                                self.closeBulkModal();
                            },
                        },
                    ];

                    this.bulk.modal.buttons.right = [
                        {
                            size: 'mini',
                            type: 'warning',
                            icon: 'el-icon-fa-ban fas',
                            label: this.$t('Remove'),
                            action: function () {
                                self.handleBulkAction('change-indicators:remove');
                            },
                        },
                        {
                            size: 'mini',
                            type: 'success',
                            icon: 'el-icon-fa-check fas',
                            label: this.$t('Add'),
                            action: function () {
                                self.handleBulkAction('change-indicators:add');
                            },
                        },
                    ];

                    this.$nextTick(function () {
                        self.bulk.modal.visibility = true;
                    });
                    break;

                case 'change-indicators:remove':
                case 'change-indicators:add':
                    this.bulk.modal.visibility = false;

                    var payload = this.bulk.modal.data.indicators;

                    useSettingsStore().updateLoading(true);
                    useSettingsStore()
                        .processPaginationBulkAction({
                            action: action,
                            payload: payload,
                        })
                        .then(function (data) {
                            if (data.actions && data.actions.indexOf('refetch-list') >= 0) {
                                self.$notify.success({
                                    title: self.$t('Bulk action processed successfully!'),
                                    iconClass: 'fas el-icon-fa-check',
                                    position: 'bottom-left',
                                    customClass: 'dark',
                                    duration: 8000,
                                });
                                apolloClient.clearStore().then(function () {
                                    self.refetch();
                                });
                            } else {
                                self.refetch();
                            }
                        })
                        .catch(function (error) {
                            console.error(error);
                            self.$notify.error({
                                title: self.$t('Error processing bulk action'),
                                iconClass: 'fas el-icon-fa-xmark',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });
                            useSettingsStore().updateLoading(false);
                        });
                    break;
            }
        },
        closeBulkModal() {
            this.bulk.modal.visibility = false;
        },
        resetBulkModal() {
            var self = this;
            this.bulk.modal = {
                visibility: false,
                width: '400px',
                title: undefined,
                body: undefined,
                data: undefined,
                buttons: {
                    left: [
                        {
                            size: 'mini',
                            icon: 'el-icon-fa-times fas',
                            label: this.$t('Dismiss'),
                            action: function () {
                                self.closeBulkModal();
                            },
                        },
                    ],
                    right: [],
                },
            };
        },
        toggleBulkModalIndicator(indicator) {
            var indicators = _.cloneDeep(this.bulk.modal.data.indicators);
            var index = indicators.findIndex(function (element) {
                return element.id === indicator.id;
            });
            if (index >= 0) {
                indicators.splice(index, 1);
            } else {
                indicators.push(indicator);
            }
            this.$set(this.bulk.modal.data, 'indicators', indicators);
        },
    },
    mounted() {
        this.fetchIndicatorOptions();
        this.refetch();
    },
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.el-button--medium.is-circle
    padding: 5px

.mimic-button
    padding: 0
    width: 28px
    height: 28px
    font-size: 0.85rem
    &[disabled]
        pointer-events: none
    &:not([disabled]):hover
        background: $--color-primary
        border-color: $--color-primary
        color: white

.account-list
    :deep()
        .column.name
            position: relative
            .is-owner
                font-size: 0.6rem
                position: absolute
                color: $--color-primary
                top: 0
                left: -10px

.text-center
    text-align: center

.condensed-indicators
    z-index: 0
    .indicator
        width: 14px
        height: 14px
        margin-bottom: -2px
        border: 2px solid $color-selection
        position: relative
        & + .indicator
            margin-left: -8px
        @for $i from 1 through 10
            &:nth-child(#{$i})
                z-index: #{10 - $i}

.bulk-modal-body.indicators
    display: flex
    align-items: center
    justify-content: center
    .indicator
        width: 30px
        height: 30px
        border-width: 2px
        font-size: 2rem
        position: relative
        i.fas
            position: absolute
            left: calc(50% - 0.45em)
            top: calc(50% - 0.45em)
            color: white
            font-size: 0.6em

.actions-row
    display: flex
    align-items: center
    gap: 5px
    .col-left
        margin-right: 10px

</style>
